import React from 'react';
import type { RouteObject } from 'react-router-dom';
import { createBrowserRouter } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { product } from 'product';
import { PublicApp } from 'PublicApp';
import { PresetApp } from 'PublicApp/PresetApp';

import App from 'App';
import { Login } from 'App/Login';
import { LoginByEntraIDCallback } from 'App/Login/EntraID/LoginByEntraIDCallback';
import { LoginByGithubCallback } from 'App/Login/LoginByGithubCallback';
import { LoginByGoogleCallback } from 'App/Login/LoginByGoogleCallback';
import { LoginByOktaCallback } from 'App/Login/LoginByOktaCallback';
import { TokenLogin } from 'App/Login/TokenLogin';
import { Logout } from 'App/Logout';
import { FleetAdd, FleetDetails, FleetList } from 'pages/Fleets';
import { EventsList as FleetEventsList } from 'pages/Fleets/Details/Events';
import { FleetDetails as FleetDetailsGeneral } from 'pages/Fleets/Details/FleetDetails';
import { FleetInspect } from 'pages/Fleets/Details/Inspect';
import { FleetOffers } from 'pages/Fleets/Details/Offers';
import { InstanceDetailsPage, InstanceList } from 'pages/Instances';
import { EventsList as InstanceEventsList } from 'pages/Instances/Details/Events';
import { InstanceInspect } from 'pages/Instances/Details/Inspect';
import { InstanceDetails } from 'pages/Instances/Details/InstanceDetails';
import { ModelsList } from 'pages/Models';
import { ModelDetails } from 'pages/Models/Details';
import {
    CreateProjectWizard,
    ProjectAdd,
    ProjectDetails,
    ProjectEvents,
    ProjectList,
    ProjectQuotas,
    ProjectSettings,
} from 'pages/Project';
import { BackendAdd, BackendEdit } from 'pages/Project/Backends';
import { AddGateway, EditGateway } from 'pages/Project/Gateways';
import {
    EventsList as RunEvents,
    JobLogs,
    JobMetrics,
    Launch,
    LaunchOptions,
    RunDetails,
    RunDetailsPage,
    RunList,
} from 'pages/Runs';
import { RunInspect } from 'pages/Runs/Details/Inspect';
import { JobDetailsPage } from 'pages/Runs/Details/Jobs/Details';
import { EventsList as JobEvents } from 'pages/Runs/Details/Jobs/Events';
import { CreditsHistoryAdd, UserAdd, UserDetails, UserEdit, UserList } from 'pages/User';
import { UserBilling, UserEvents, UserProjects, UserSettings } from 'pages/User/Details';

import { AuthErrorMessage } from './App/AuthErrorMessage';
import { EventList } from './pages/Events';
import { OfferList } from './pages/Offers';
import { PresetDeploy, PresetDetails, PresetDetailsOverview, PresetInspect, PresetList } from './pages/Presets';
import { JobDetails } from './pages/Runs/Details/Jobs/Details/JobDetails';
import { VolumeList } from './pages/Volumes';
import { ROUTES } from './routes';

export const router = createBrowserRouter([
    ...(product.hasPresets
        ? [
              {
                  element: <PublicApp />,
                  errorElement: <AuthErrorMessage title="Not Found" text="Page not found" />,
                  children: [
                      { path: ROUTES.BASE, element: <Login /> },
                      { path: ROUTES.AUTH.TOKEN, element: <TokenLogin /> },
                  ],
              },
              {
                  element: <PresetApp />,
                  errorElement: <AuthErrorMessage title="Not Found" text="Page not found" />,
                  children: [
                      {
                          path: ROUTES.PRESETS.LIST,
                          element: <PresetList />,
                      },
                      {
                          path: ROUTES.PRESETS.DETAILS.TEMPLATE,
                          element: <PresetDetails />,
                          children: [
                              { index: true, element: <PresetDetailsOverview /> },
                              { path: ROUTES.PRESETS.DETAILS.DEPLOY.TEMPLATE, element: <PresetDeploy /> },
                              { path: 'verified-on', element: <Navigate to=".." replace /> },
                              { path: ROUTES.PRESETS.DETAILS.INSPECT.TEMPLATE, element: <PresetInspect /> },
                          ],
                      },
                  ],
              },
          ]
        : []),
    {
        path: '/',
        element: <App />,
        errorElement: <AuthErrorMessage title="Not Found" text="Page not found" />,
        children: [
            // auth
            {
                path: ROUTES.AUTH.GITHUB_CALLBACK,
                element: <LoginByGithubCallback />,
            },
            {
                path: ROUTES.AUTH.OKTA_CALLBACK,
                element: <LoginByOktaCallback />,
            },
            {
                path: ROUTES.AUTH.ENTRA_CALLBACK,
                element: <LoginByEntraIDCallback />,
            },
            {
                path: ROUTES.AUTH.GOOGLE_CALLBACK,
                element: <LoginByGoogleCallback />,
            },
            ...(!product.hasPresets ? [{ path: ROUTES.AUTH.TOKEN, element: <TokenLogin /> }] : []),
            // hubs
            ...(!product.hasPresets ? [{ path: ROUTES.BASE, element: <Navigate replace to={ROUTES.RUNS.LIST} /> }] : []),
            {
                path: ROUTES.PROJECT.LIST,
                element: <ProjectList />,
            },
            {
                path: ROUTES.PROJECT.DETAILS.TEMPLATE,
                element: <ProjectDetails />,
                children: [
                    {
                        index: true,
                        element: <ProjectSettings />,
                    },
                    product.hasBilling && {
                        path: ROUTES.PROJECT.DETAILS.QUOTAS.TEMPLATE,
                        element: <ProjectQuotas />,
                    },
                    product.hasEvents && {
                        path: ROUTES.PROJECT.DETAILS.EVENTS.TEMPLATE,
                        element: <ProjectEvents />,
                    },
                    {
                        path: ROUTES.PROJECT.BACKEND.ADD.TEMPLATE,
                        element: <BackendAdd />,
                    },
                    {
                        path: ROUTES.PROJECT.BACKEND.EDIT.TEMPLATE,
                        element: <BackendEdit />,
                    },
                    {
                        path: ROUTES.PROJECT.GATEWAY.ADD.TEMPLATE,
                        element: <AddGateway />,
                    },
                    {
                        path: ROUTES.PROJECT.GATEWAY.EDIT.TEMPLATE,
                        element: <EditGateway />,
                    },
                ].filter(Boolean) as RouteObject[],
            },
            {
                path: ROUTES.PROJECT.DETAILS.RUNS.DETAILS.TEMPLATE,
                element: <RunDetailsPage />,
                children: [
                    {
                        index: true,
                        element: <RunDetails />,
                    },
                    {
                        path: ROUTES.PROJECT.DETAILS.RUNS.DETAILS.METRICS.TEMPLATE,
                        element: <JobMetrics />,
                    },
                    {
                        path: ROUTES.PROJECT.DETAILS.RUNS.DETAILS.LOGS.TEMPLATE,
                        element: <JobLogs />,
                    },
                    {
                        path: ROUTES.PROJECT.DETAILS.RUNS.DETAILS.EVENTS.TEMPLATE,
                        element: <RunEvents />,
                    },
                    {
                        path: ROUTES.PROJECT.DETAILS.RUNS.DETAILS.INSPECT.TEMPLATE,
                        element: <RunInspect />,
                    },
                ],
            },
            {
                path: ROUTES.PROJECT.DETAILS.RUNS.DETAILS.JOBS.DETAILS.TEMPLATE,
                element: <JobDetailsPage />,
                children: [
                    {
                        index: true,
                        element: <JobDetails />,
                    },
                    {
                        path: ROUTES.PROJECT.DETAILS.RUNS.DETAILS.JOBS.DETAILS.METRICS.TEMPLATE,
                        element: <JobMetrics />,
                    },
                    {
                        path: ROUTES.PROJECT.DETAILS.RUNS.DETAILS.JOBS.DETAILS.LOGS.TEMPLATE,
                        element: <JobLogs />,
                    },
                    {
                        path: ROUTES.PROJECT.DETAILS.RUNS.DETAILS.JOBS.DETAILS.EVENTS.TEMPLATE,
                        element: <JobEvents />,
                    },
                ],
            },

            ...([
                !product.hasBilling && {
                    path: ROUTES.PROJECT.ADD,
                    element: <ProjectAdd />,
                },
                product.hasBilling && {
                    path: ROUTES.PROJECT.ADD,
                    element: <CreateProjectWizard />,
                },
            ].filter(Boolean) as RouteObject[]),

            // Runs
            {
                path: ROUTES.RUNS.LIST,
                element: <RunList />,
            },

            {
                path: ROUTES.RUNS.LAUNCH,
                element: <LaunchOptions />,
            },

            ...(product.hasTemplates ? [{ path: ROUTES.RUNS.CREATE_DEV_ENV, element: <Launch /> }] : []),

            // Offers
            {
                path: ROUTES.OFFERS.LIST,
                element: <OfferList />,
            },

            // Models
            {
                path: ROUTES.MODELS.LIST,
                element: <ModelsList />,
            },
            {
                path: ROUTES.MODELS.DETAILS.TEMPLATE,
                element: <ModelDetails />,
            },

            // Events, Enterprise and above
            ...([
                product.hasEvents && {
                    path: ROUTES.EVENTS.LIST,
                    element: <EventList />,
                },
            ].filter(Boolean) as RouteObject[]),

            // Fleets
            {
                path: ROUTES.FLEETS.LIST,
                element: <FleetList />,
            },
            {
                path: ROUTES.FLEETS.ADD.TEMPLATE,
                element: <FleetAdd />,
            },
            {
                path: ROUTES.FLEETS.DETAILS.TEMPLATE,
                element: <FleetDetails />,
                children: [
                    {
                        index: true,
                        element: <FleetDetailsGeneral />,
                    },
                    {
                        path: ROUTES.FLEETS.DETAILS.OFFERS.TEMPLATE,
                        element: <FleetOffers />,
                    },
                    {
                        path: ROUTES.FLEETS.DETAILS.EVENTS.TEMPLATE,
                        element: <FleetEventsList />,
                    },
                    {
                        path: ROUTES.FLEETS.DETAILS.INSPECT.TEMPLATE,
                        element: <FleetInspect />,
                    },
                ],
            },

            // Instances
            {
                path: ROUTES.INSTANCES.LIST,
                element: <InstanceList />,
            },
            {
                path: ROUTES.INSTANCES.DETAILS.TEMPLATE,
                element: <InstanceDetailsPage />,
                children: [
                    {
                        index: true,
                        element: <InstanceDetails />,
                    },
                    {
                        path: ROUTES.INSTANCES.DETAILS.EVENTS.TEMPLATE,
                        element: <InstanceEventsList />,
                    },
                    {
                        path: ROUTES.INSTANCES.DETAILS.INSPECT.TEMPLATE,
                        element: <InstanceInspect />,
                    },
                ],
            },

            // Volumes
            {
                path: ROUTES.VOLUMES.LIST,
                element: <VolumeList />,
            },

            // Users
            {
                path: ROUTES.USER.LIST,
                element: <UserList />,
            },
            {
                path: ROUTES.USER.ADD,
                element: <UserAdd />,
            },
            {
                path: ROUTES.USER.DETAILS.TEMPLATE,
                element: <UserDetails />,
                children: [
                    {
                        index: true,
                        element: <UserSettings />,
                    },
                    {
                        path: ROUTES.USER.PROJECTS.TEMPLATE,
                        element: <UserProjects />,
                    },
                    product.hasEvents && {
                        path: ROUTES.USER.EVENTS.TEMPLATE,
                        element: <UserEvents />,
                    },
                    {
                        // SSH keys moved to a section inside the Settings tab
                        path: ROUTES.USER.PUBLIC_KEYS.TEMPLATE,
                        element: <Navigate replace to=".." />,
                    },
                    product.hasBilling && {
                        path: ROUTES.USER.BILLING.LIST.TEMPLATE,
                        element: <UserBilling />,
                    },
                ].filter(Boolean) as RouteObject[],
            },
            {
                path: ROUTES.USER.EDIT.TEMPLATE,
                element: <UserEdit />,
            },
            {
                path: ROUTES.USER.BILLING.ADD_PAYMENT.TEMPLATE,
                element: <CreditsHistoryAdd />,
            },

            // auth
            {
                path: ROUTES.LOGOUT,
                element: <Logout />,
            },
        ],
    },
]);
